import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Contact";
import { PageHero } from "@/components/site/PageHero";
import { ArrowRight, ChevronDown, FileText, Workflow, Clock, Sparkles } from "lucide-react";

export default function Services() {
  const [services, setServices] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [filter, setFilter] = useState("All");

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Services | LaunchLab";
    api.get("/portal/services").then((r) => setServices(r.data)).catch(() => setServices([]));
  }, []);

  const categories = useMemo(() => ["All", ...new Set((services || []).map((s) => s.category).filter(Boolean))], [services]);
  const visible = useMemo(() => (services || []).filter((s) => filter === "All" || s.category === filter), [services, filter]);

  return (
    <div data-testid="services-page" className="relative w-full max-w-[100vw] overflow-x-clip text-slate-100 min-h-screen">
      <Header />
      <PageHero
        testid="services-hero"
        kicker="Services Catalog"
        title="EVERYTHING YOUR"
        highlight="STARTUP NEEDS."
        subtext="Registration to launch — every service is handled end-to-end by a dedicated LaunchLab specialist, tracked live in your founder portal."
      />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="flex flex-wrap gap-2 mb-10" data-testid="services-filters">
          {categories.map((c) => (
            <button
              key={c}
              data-testid={`services-filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              onClick={() => setFilter(c)}
              className={`px-4 py-2 rounded-full text-xs font-semibold transition-colors ${
                filter === c ? "bg-gradient-to-r from-[#FF8800] to-[#ff5e00] text-[#06080E]" : "glass text-slate-400 hover:text-white"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {services === null ? (
          <div className="flex justify-center py-20"><div className="w-10 h-10 rounded-full border-2 border-[#FF8800] border-t-transparent animate-spin" /></div>
        ) : visible.length === 0 ? (
          <div className="glass rounded-2xl p-14 text-center text-slate-400" data-testid="services-empty">Services will be published soon.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="services-grid">
            {visible.map((sv, i) => {
              const isOpen = expanded === sv.id;
              return (
                <motion.div
                  key={sv.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.5, delay: (i % 4) * 0.07 }}
                  data-testid={`service-card-${sv.id}`}
                  className="card-beam glass rounded-2xl p-6 sm:p-7 flex flex-col transition-all duration-300 hover:-translate-y-1 hover:border-[#FF8800]/40"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="font-display text-xl font-bold text-white leading-snug">{sv.name}</h3>
                      <div className="flex flex-wrap gap-2 mt-2">
                        <span className="text-[10px] font-mono-tech px-2.5 py-1 rounded-full border border-[#00B0FF]/40 text-[#00B0FF] uppercase tracking-wider">{sv.category}</span>
                        {sv.specialist_category && (
                          <span className="text-[10px] font-mono-tech px-2.5 py-1 rounded-full border border-[#00E676]/40 text-[#00E676] uppercase tracking-wider">{sv.specialist_category}</span>
                        )}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="font-display text-2xl font-extrabold text-gradient-saffron">₹{Number(sv.price).toLocaleString("en-IN")}</p>
                      <p className="text-[10px] text-slate-500 font-mono-tech">+ {sv.gst_percent}% GST</p>
                    </div>
                  </div>

                  <p className="mt-3 text-sm text-slate-400 leading-relaxed">{sv.description}</p>
                  {sv.who_needs && <p className="mt-2 text-xs text-slate-500 italic">For: {sv.who_needs}</p>}

                  <div className="flex items-center gap-4 mt-4 text-[11px] font-mono-tech text-slate-500">
                    {sv.processing_days && <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-[#00B0FF]" /> {sv.processing_days}</span>}
                    {(sv.documents_required || []).length > 0 && <span className="flex items-center gap-1.5"><FileText className="w-3.5 h-3.5 text-[#FF8800]" /> {sv.documents_required.length} documents</span>}
                    {(sv.workflow || []).length > 0 && <span className="flex items-center gap-1.5"><Workflow className="w-3.5 h-3.5 text-[#00E676]" /> {sv.workflow.length} tracked steps</span>}
                  </div>

                  <button
                    onClick={() => setExpanded(isOpen ? null : sv.id)}
                    data-testid={`service-expand-${sv.id}`}
                    className="mt-4 flex items-center gap-1.5 text-xs text-slate-400 hover:text-[#FF8800] transition-colors self-start"
                  >
                    Details <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                  </button>

                  <AnimatePresence>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                        data-testid={`service-details-${sv.id}`}
                      >
                        <div className="mt-4 pt-4 border-t border-white/10 space-y-4">
                          {(sv.documents_required || []).length > 0 && (
                            <div>
                              <p className="font-mono-tech text-[10px] tracking-[0.25em] text-[#FF8800] uppercase mb-2">Documents you'll need</p>
                              <div className="flex flex-wrap gap-1.5">
                                {sv.documents_required.map((d) => (
                                  <span key={d} className="text-[11px] px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-slate-300">{d}</span>
                                ))}
                              </div>
                            </div>
                          )}
                          {(sv.workflow || []).length > 0 && (
                            <div>
                              <p className="font-mono-tech text-[10px] tracking-[0.25em] text-[#00B0FF] uppercase mb-2">How it progresses</p>
                              <ol className="space-y-1.5">
                                {sv.workflow.map((w, wi) => (
                                  <li key={wi} className="flex items-start gap-2.5 text-xs text-slate-400">
                                    <span className="font-mono-tech text-[#00B0FF] shrink-0">{String(wi + 1).padStart(2, "0")}</span>
                                    {w.name || w}
                                  </li>
                                ))}
                              </ol>
                            </div>
                          )}
                          {sv.terms && <p className="text-[11px] text-slate-500 leading-relaxed">{sv.terms}</p>}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <div className="mt-5 pt-5 border-t border-white/10">
                    <Link
                      to={`/portal?service=${sv.id}&utm_source=services_page&utm_medium=cta`}
                      data-testid={`service-cta-${sv.id}`}
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-[#FF8800] to-[#ff5e00] text-[#06080E] font-semibold text-sm transition-transform hover:scale-105"
                    >
                      Start with this service <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="glass rounded-2xl p-10 sm:p-14 text-center mt-16"
          data-testid="services-ai-cta"
        >
          <Sparkles className="w-8 h-8 text-[#FF8800] mx-auto mb-4" />
          <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-white">Not sure where to start?</h2>
          <p className="mt-3 text-sm text-slate-400 max-w-xl mx-auto">
            Register your startup idea in the LaunchLab portal — our AI validates it, scores it, and recommends exactly which of these services you need, in what order.
          </p>
          <Link
            to="/portal"
            data-testid="services-ai-cta-button"
            className="inline-flex items-center gap-2 mt-7 px-8 py-3.5 rounded-full bg-gradient-to-r from-[#FF8800] to-[#ff5e00] text-[#06080E] font-semibold text-sm transition-transform hover:scale-105"
          >
            Get Your Free AI Validation <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>
      </section>

      <Footer />
    </div>
  );
}
